import {
  Component,
  ElementRef,
  EventEmitter,
  HostListener,
  Input,
  OnInit,
  Output,
  ViewChild
} from '@angular/core';
import { navItems, navItems1, navItems2 } from './sidebar-data';
import { NavService } from '../../../services/nav.service';
import { NavItem2 } from './nav-item/nav-item';
import { ApiService } from 'src/app/services/api.service';
import { AuthorizationService } from '../../../services/authorization.service';

interface UserProfile {
  firstName: string;
  middleName: string;
  lastName: string;
  email: string;
  userType: string;
  avatar: string;
}

@Component({
  selector: 'app-sidebar',
  templateUrl: './sidebar.component.html'
})
export class SidebarComponent implements OnInit {
  @Input() collapsed = false;
  @Output() collapsedChange = new EventEmitter<boolean>();
  @ViewChild('fileInput') fileInput!: ElementRef<HTMLInputElement>;

  // Navigation
  filteredNavItems: NavItem2[] = [];

  // User Profile
  userProfile: UserProfile = {
    firstName: 'Ampsys',
    middleName: '',
    lastName: '',
    email: '',
    userType: '',
    avatar: ''
  };

  // UI State
  profileCardOpen = false;
  isKebabOpen = false;
  selectedFile: File | null = null;
  base64File: string = '';
  fileName: string = '';

  constructor(
    private navService: NavService,
    private apiService: ApiService,
    private authorizationService: AuthorizationService
  ) {}

  ngOnInit(): void {
    this.initializeSidebar();
    this.loadUserProfile();
    this.loadNavigationItems();
  }

  /**
   * Initialize sidebar state from localStorage
   */
  private initializeSidebar(): void {
    const savedState = localStorage.getItem('sidebarCollapsed');
    if (savedState !== null) {
      this.collapsed = JSON.parse(savedState);
      this.collapsedChange.emit(this.collapsed);
    }
  }

  /**
   * Load user profile from session storage
   */
  private loadUserProfile(): void {
    const userTypeRaw = sessionStorage.getItem('UserType');

    if (!userTypeRaw) {
      this.setAvatarFromName(this.userProfile.firstName);
      return;
    }

    try {
      this.userProfile.userType = JSON.parse(atob(userTypeRaw));
      this.userProfile.email = sessionStorage.getItem('CustomerLoginEmail') ?? '';
      
      // Load user name if available
      const firstNameRaw = sessionStorage.getItem('FirstName');
      const lastNameRaw = sessionStorage.getItem('LastName');
      
      if (firstNameRaw) {
        this.userProfile.firstName = JSON.parse(atob(firstNameRaw));
      }
      if (lastNameRaw) {
        this.userProfile.lastName = JSON.parse(atob(lastNameRaw));
      }

      this.setAvatarFromName(this.getFullName());
    } catch (error) {
      console.error('Error loading user profile:', error);
      this.setAvatarFromName(this.userProfile.firstName);
    }
  }

  /**
   * Load and filter navigation items based on user type and permissions
   */
  private loadNavigationItems(): void {
    switch (this.userProfile.userType) {
      case 'Admin':
        this.filteredNavItems = this.getAdminNavItems();
        break;
      case 'Customer':
        this.filteredNavItems = this.getCustomerNavItems();
        break;
      default:
        this.filteredNavItems = navItems1;
    }
  }

  private canShowNavItem(item: NavItem2): boolean {
    return item.permissionKeys
      ? this.authorizationService.canNavAny(item.permissionKeys)
      : this.authorizationService.canNav(item.permissionKey!);
  }

  private getAdminNavItems(): NavItem2[] {
    return navItems
      .filter(item => this.canShowNavItem(item))
      .map(item =>
        item.permissionKeys
          ? { ...item, route: this.authorizationService.getAdminOpsLandingRoute(item.permissionKeys) ?? item.route }
          : item
      );
  }

  private getCustomerNavItems(): NavItem2[] {
    const customerLogins = sessionStorage.getItem('CustomerLoginsProfile') === 'True';

    return navItems2.filter(item => {
      const isProfile = item.PageName === 'Profile';
      // Customer Login = false → show ONLY Profile
      if (!customerLogins) {
        return isProfile;
      }
      // Customer Login = true → show every permitted item except Profile
      return !isProfile && this.canShowNavItem(item);
    });
  }

  /**
   * Toggle sidebar collapse state
   */
  toggleCollapse(): void {
    this.collapsed = !this.collapsed;
    localStorage.setItem('sidebarCollapsed', JSON.stringify(this.collapsed));
    this.collapsedChange.emit(this.collapsed);
  }

  /**
   * Toggle profile card visibility
   */
  toggleProfileCard(event: MouseEvent): void {
    event.stopPropagation();
    this.profileCardOpen = !this.profileCardOpen;
  }

  /**
   * Toggle kebab menu visibility
   */
  toggleKebabMenu(event: MouseEvent): void {
    event.stopPropagation();
    this.isKebabOpen = !this.isKebabOpen;
  }

  /**
   * Trigger file input for avatar upload
   */
  onEditClick(event: MouseEvent): void {
    event.stopPropagation();
    this.fileInput.nativeElement.click();
  }

  /**
   * Handle file selection for avatar upload
   */
  onFileSelected(event: Event): void {
    const input = event.target as HTMLInputElement;
    if (input.files && input.files.length > 0) {
      this.handleFileUpload(input.files[0]);
    }
  }

  /**
   * Validate and process file upload
   */
  private handleFileUpload(file: File): void {
    const maxSizeInBytes = 5 * 1024 * 1024; // 5MB
    const allowedTypes = ['application/pdf', 'image/jpeg', 'image/png', 'image/jpg'];

    // Validate file type
    if (!allowedTypes.includes(file.type)) {
      console.error('Invalid file type');
      this.resetFileInput();
      return;
    }

    // Validate file size
    if (file.size > maxSizeInBytes) {
      console.error('File size exceeds 5MB limit');
      this.resetFileInput();
      return;
    }

    // Convert to base64
    const reader = new FileReader();
    reader.onload = () => {
      const base64String = (reader.result as string).split(',')[1];
      this.base64File = base64String;
      this.fileName = file.name;
      this.updateAvatar();
    };
    reader.onerror = () => {
      console.error('Error reading file');
      this.resetFileInput();
    };
    reader.readAsDataURL(file);
  }

  /**
   * Update avatar (call API or update locally)
   */
  private updateAvatar(): void {
    // There is no avatar endpoint yet, so the avatar is only updated locally.
    this.userProfile.avatar = `data:image/png;base64,${this.base64File}`;
    this.resetFileInput();
  }

  /**
   * Reset file input
   */
  private resetFileInput(): void {
    this.fileInput.nativeElement.value = '';
    this.selectedFile = null;
  }

  /**
   * Get user's full name
   */
  getFullName(): string {
    const { firstName, middleName, lastName } = this.userProfile;
    return [firstName, middleName, lastName]
      .filter(name => name?.trim())
      .join(' ')
      .trim() || 'User';
  }

  /**
   * Get user email
   */
  get userEmail(): string {
    return this.userProfile.email || 'user@example.com';
  }

  /**
   * Set avatar from name initials
   */
  private setAvatarFromName(name: string): void {
    const initials = this.getInitials(name);
    this.userProfile.avatar = `https://ui-avatars.com/api/?name=${initials}&background=ffffff&color=4F46E5&size=128&bold=true`;
  }

  /**
   * Extract initials from name
   */
  private getInitials(name: string): string {
    return name
      .split(' ')
      .map(n => n.charAt(0).toUpperCase())
      .join('')
      .slice(0, 2);
  }

  /**
   * Logout user
   */
  logout(event: MouseEvent): void {
    event.preventDefault();
    this.authorizationService.clear();
    sessionStorage.clear();
    // Navigate to login or handle logout
  }

  /**
   * Close profile card when clicking outside
   */
  @HostListener('document:click', ['$event'])
  closeProfileCard(event: Event): void {
    const target = event.target as HTMLElement;
    const isInsideSidebar = target.closest('.sidebar-footer') ?? target.closest('.profile-card');
    
    if (!isInsideSidebar) {
      this.profileCardOpen = false;
      this.isKebabOpen = false;
    }
  }
}